import { makeAutoObservable, runInAction } from "mobx";
import { InfiniteQueryObserver, isCancelledError, type InfiniteData } from "@tanstack/react-query";
import { pollExportStatus } from "@/components/data-table/use-csv-export";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import { QUERY_CACHE_FLAG } from "@/lib/launchdarkly/flags";
import { getQueryClient, peekQueryClient } from "@/queries/client";
import { invalidateScoped } from "@/queries/invalidation";
import {
  isMemberBankKey,
  isMembersPageKey,
  memberBankQuery,
  memberEligibleRolesKey,
  memberEligibleRolesQuery,
  membersPageQuery,
} from "@/queries/members";
import { FRESHNESS } from "@/queries/policies";
import { scopedKey, scopeFor, ScopeNotReadyError, type QueryScope } from "@/queries/scope";
import { invalidateTeamRosters, teamIdOfRosterKey } from "@/queries/teams";
import type {
  IWorkspaceMembersListQuery,
  IWorkspaceMembersPage,
} from "@/services/workspace.service";
import type { RootStore } from "./root.store";
import type {
  IWorkspaceBulkInviteFormData,
  IWorkspaceMember,
  IWorkspaceMemberInvitation,
} from "@/types/workspace";
import type { TUserImportRequest, TUserImportResult } from "@/types/user-import";

export interface IWorkspaceMemberFilters {
  search: string;
  role: "all" | "admin" | "member" | "guest";
}

export interface IRoleBlocking {
  role: string;
  resource_type: string;
  resource_id: string;
  resource_name: string;
}

interface IRoleEligibility {
  eligible: boolean;
  blocking_roles: IRoleBlocking[];
}

/** Per-role-slug eligibility for a single member. */
export type MemberRoleEligibility = Record<string, IRoleEligibility>;

export class WorkspaceMembersStore {
  members: IWorkspaceMember[] = [];
  invitations: IWorkspaceMemberInvitation[] = [];
  filters: IWorkspaceMemberFilters = {
    search: "",
    role: "all",
  };
  isLoading = false;
  // Last members-load failure, surfaced to the DataTable `error` prop. Cleared
  // when a (re)fetch starts; set when fetchMembers rejects.
  loadError: Error | null = null;

  // --- Paginated management-list cache (parallel to `members`) ---------------
  // The settings Members table is server-driven (filters/sort/pagination); the
  // full `members` list above stays the deduped bank every member-picker uses.
  pagedMembers: IWorkspaceMember[] = [];
  pageInfo: { totalCount: number; hasNext: boolean; nextCursor: string | null } = {
    totalCount: 0,
    hasNext: false,
    nextCursor: null,
  };
  isPageLoading = false;
  /** Count of first-page loads currently in flight; drives `isPageLoading` so a
   *  superseded load bailing out can't clear the spinner while a newer one runs. */
  private inFlightPageLoads = 0;
  isLoadingMore = false;
  pageError: Error | null = null;
  private lastPageQuery: IWorkspaceMembersListQuery = {};
  /** Monotonic token for first-page loads. A load only applies its result if it
   *  is still the latest — otherwise a slow in-flight page fetch (e.g. the
   *  search-debounce reload) can resolve *after* a member PATCH and overwrite
   *  `pagedMembers` with a page that predates the write, reverting the just-set
   *  value in the grid (the job-role E1 flake, #2368). */
  private pageLoadSeq = 0;
  isInvitationsLoading = false;
  // Surfaced by the Pending Invitations tab's error view (NG-2490); the old
  // stacked-cards panel never read this and rendered empty on a failure.
  invitationsLoadError: Error | null = null;
  isInviting = false;
  activeMutationMemberId: string | null = null;
  activeInvitationId: string | null = null;
  /** Lazily-fetched per-member role eligibility (keyed by member id). Populated
   *  on demand when a role picker opens; cleared for a member after their role
   *  changes so the next open re-fetches. */
  roleEligibility = new Map<string, MemberRoleEligibility>();

  private rootStore: RootStore;
  /** In-flight eligibility fetches, to dedupe concurrent picker opens. */
  private pendingEligibility = new Set<string>();
  /** Role changes per member, so a flag-on eligibility check that started
   *  before one cannot apply its answer after it. */
  private eligibilityWrites = new Map<string, number>();
  /** Slug whose member bank has been (or is being) loaded via ensureMembers. */
  private membersEnsuredFor: string | null = null;
  /** The cold-cache load started by `ensureMembers`. A second consumer must
   *  await this same request instead of mistaking `isLoading` for "settled". */
  private membersEnsurePromise: Promise<void> | null = null;
  private membersEnsureSlug: string | null = null;
  /** The cached bank (R20) `members` was last projected from, so a fresh-cache
   *  read that changes nothing writes nothing and a per-render caller stays
   *  free. Raw, not observable: MobX copies an array it is handed. */
  private projectedBank: IWorkspaceMember[] | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        pendingEligibility: false,
        eligibilityWrites: false,
        lastPageQuery: false,
        pageLoadSeq: false,
        inFlightPageLoads: false,
        membersEnsuredFor: false,
        membersEnsurePromise: false,
        membersEnsureSlug: false,
        projectedBank: false,
      } as never,
      { autoBind: true },
    );
  }

  /** Idempotent cold-cache fetch for member pickers; safe to call per-render.
   *  Concurrent consumers share the in-flight promise so "ensure completed"
   *  always means the member bank has actually settled.
   *
   *  With the `query-cache` flag on it is {@link loadMembers}: the cached bank
   *  (R20) answers while it is fresh, and the guards below are not used. */
  ensureMembers(slug: string): Promise<void> {
    if (this.bankScope(slug)) {
      return this.loadMembers(slug)
        .then(() => undefined)
        .catch((err: unknown) => {
          console.error(`[workspace-members] ensureMembers failed for workspace ${slug}`, err);
        });
    }

    if (this.membersEnsuredFor === slug && this.members.length > 0) {
      return Promise.resolve();
    }

    if (this.membersEnsurePromise) {
      if (this.membersEnsureSlug === slug) return this.membersEnsurePromise;
      return this.membersEnsurePromise.then(() => this.ensureMembers(slug));
    }

    this.membersEnsuredFor = slug;
    this.membersEnsureSlug = slug;
    const promise = this.fetchMembers(slug)
      .then(() => undefined)
      .catch((err) => {
        console.error(`[workspace-members] ensureMembers failed for workspace ${slug}`, err);
      })
      .finally(() => {
        if (this.membersEnsurePromise === promise) {
          this.membersEnsurePromise = null;
          this.membersEnsureSlug = null;
        }
      });
    this.membersEnsurePromise = promise;
    return promise;
  }

  get filteredMembers(): IWorkspaceMember[] {
    const search = this.filters.search.trim().toLowerCase();

    return this.members.filter((member) => {
      const matchesSearch =
        search.length === 0 ||
        member.member.display_name.toLowerCase().includes(search) ||
        member.member.email?.toLowerCase().includes(search);

      if (!matchesSearch) {
        return false;
      }

      if (this.filters.role === "all") {
        return true;
      }

      if (this.filters.role === "admin") {
        return member.role === "workspace.admin";
      }

      if (this.filters.role === "member") {
        return member.role === "workspace.standardUser";
      }

      return member.role === "workspace.guest";
    });
  }

  setFilter<K extends keyof IWorkspaceMemberFilters>(
    key: K,
    value: IWorkspaceMemberFilters[K],
  ): void {
    this.filters[key] = value;
  }

  /** A member's role eligibility, checked when a role picker opens. With the
   *  `query-cache` flag off it is fetched once per member and reused for the
   *  life of the page; with it on every open asks again (R22, see
   *  `fetchCachedEligibleRoles`). Either way concurrent opens share one
   *  request, and a failure leaves all roles enabled. */
  async fetchEligibleRoles(slug: string, memberId: string): Promise<void> {
    const scope = this.bankScope(slug);
    if (scope) return this.fetchCachedEligibleRoles(slug, scope, memberId);
    if (this.roleEligibility.has(memberId) || this.pendingEligibility.has(memberId)) return;
    this.pendingEligibility.add(memberId);
    try {
      const roles = await this.rootStore.workspaceService.fetchMemberEligibleRoles(slug, memberId);
      runInAction(() => {
        this.roleEligibility.set(memberId, roles);
      });
    } catch (err) {
      // Graceful degradation: leave all roles enabled on failure.
      console.warn(
        "[workspace-members] role eligibility fetch failed; leaving roles enabled",
        { slug, memberId },
        err,
      );
    } finally {
      this.pendingEligibility.delete(memberId);
    }
  }

  /**
   * The flag-on eligibility check (R22). It is access-sensitive, so every
   * picker open asks again; opens that overlap share one request, and the
   * last answer keeps the picker's roles shown while the new one is in
   * flight. Failure degrades the same way: all roles stay enabled.
   */
  private async fetchCachedEligibleRoles(
    slug: string,
    scope: QueryScope,
    memberId: string,
  ): Promise<void> {
    const writesAtRequest = this.eligibilityWrites.get(memberId) ?? 0;
    try {
      const roles = await getQueryClient().fetchQuery(
        memberEligibleRolesQuery(this.rootStore.workspaceService, scope, memberId),
      );
      if ((this.eligibilityWrites.get(memberId) ?? 0) !== writesAtRequest) return;
      runInAction(() => {
        this.roleEligibility.set(memberId, roles);
      });
    } catch (err) {
      console.warn(
        "[workspace-members] role eligibility fetch failed; leaving roles enabled",
        { slug, memberId },
        err,
      );
    }
  }

  /** A role change can shift which roles are (in)eligible: forget the answer. */
  private forgetEligibility(slug: string, memberId: string): void {
    this.roleEligibility.delete(memberId);
    this.eligibilityWrites.set(memberId, (this.eligibilityWrites.get(memberId) ?? 0) + 1);
    const scope = this.bankScope(slug);
    if (!scope) return;
    // R22 by its full key: `('member', mid)` is also the prefix of the
    // member's team list (R19), which a role change does not touch.
    peekQueryClient()?.removeQueries({
      queryKey: memberEligibleRolesKey(scope, memberId),
      exact: true,
    });
  }

  isRoleDisabled(memberId: string, role: string): boolean {
    const entry = this.roleEligibility.get(memberId)?.[role];
    return entry ? !entry.eligible : false;
  }

  getBlockingRoles(memberId: string, role: string): IRoleBlocking[] {
    const entry = this.roleEligibility.get(memberId)?.[role];
    return entry && !entry.eligible ? (entry.blocking_roles ?? []) : [];
  }

  patchMember(memberId: string, patch: Partial<IWorkspaceMember>): void {
    const apply = (m: IWorkspaceMember) => (m.id === memberId ? { ...m, ...patch } : m);
    this.members = this.members.map(apply);
    this.pagedMembers = this.pagedMembers.map(apply);
    this.patchCachedBanks((members) => members.map(apply));
    this.patchCachedPages((page) => ({ ...page, results: page.results.map(apply) }));
  }

  replaceMember(member: IWorkspaceMember): void {
    const apply = (existing: IWorkspaceMember) => (existing.id === member.id ? member : existing);
    this.members = this.members.map(apply);
    this.pagedMembers = this.pagedMembers.map(apply);
    this.patchCachedBanks((members) => members.map(apply));
    this.patchCachedPages((page) => ({ ...page, results: page.results.map(apply) }));
  }

  /**
   * The member bank a page needs on arrival. With the `query-cache` flag off
   * this is {@link fetchMembers}, unchanged. With it on, the read goes through
   * the cached bank (R20, `app/queries/members.ts`): while the entry is fresh
   * it answers with no request and no loading flip, concurrent callers share
   * one request, and the answer is projected into `members`, which every
   * picker still renders from.
   *
   * Use `fetchMembers` for a reload that must not be served from cache.
   */
  loadMembers(slug: string): Promise<IWorkspaceMember[]> {
    const scope = this.bankScope(slug);
    if (!scope) return this.fetchMembers(slug);
    const client = getQueryClient();
    const query = memberBankQuery(this.rootStore.workspaceService, scope);
    const entry = client
      .getQueryCache()
      .find<IWorkspaceMember[]>({ queryKey: query.queryKey, exact: true });
    const cached = entry?.state.data;
    if (cached !== undefined && !entry?.isStaleByTime(FRESHNESS.reference.staleTime)) {
      this.projectBank(cached);
      return Promise.resolve(cached);
    }
    return this.readCachedBank(() => client.fetchQuery(query));
  }

  /**
   * The member list for a caller that keeps its own copy instead of rendering
   * `members`: a lookup table, a dialog's options, another store's fallback.
   * With the `query-cache` flag off it is the service's GET, exactly as those
   * callers made it, and `members` is untouched. With it on it is
   * {@link loadMembers}, so the caller shares the cached bank (R20) rather
   * than adding a request of its own.
   */
  readMembers(slug: string): Promise<IWorkspaceMember[]> {
    if (!this.bankScope(slug)) return this.rootStore.workspaceService.listMembers(slug);
    return this.loadMembers(slug);
  }

  /**
   * Read the whole member bank. With the `query-cache` flag on it is still a
   * forced read, but through the cached bank (R20), so the entry every
   * `loadMembers` caller reads is the newest answer there is. A read already
   * in flight is cancelled and restarted rather than joined, because it may
   * have started before a write this caller is reloading after; its waiters
   * receive the restarted read's answer. A cold read in flight, with nothing
   * cached yet, is joined: Query restarts only an entry that has data.
   */
  async fetchMembers(slug: string): Promise<IWorkspaceMember[]> {
    const scope = this.bankScope(slug);
    if (scope) {
      const client = getQueryClient();
      const options = client.defaultQueryOptions(
        memberBankQuery(this.rootStore.workspaceService, scope),
      );
      return this.readCachedBank(() =>
        client.getQueryCache().build(client, options).fetch(options, { cancelRefetch: true }),
      );
    }
    this.isLoading = true;
    this.loadError = null;
    try {
      const members = await this.rootStore.workspaceService.listMembers(slug);
      runInAction(() => {
        this.members = members;
      });
      return members;
    } catch (err) {
      runInAction(() => {
        this.loadError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  private get queryCacheEnabled(): boolean {
    return this.rootStore.featureFlags.isEnabled(QUERY_CACHE_FLAG);
  }

  /** The scope to cache the bank under, or `null` to read the flag-off way. */
  private bankScope(slug: string): QueryScope | null {
    if (!this.queryCacheEnabled) return null;
    try {
      return scopeFor(slug);
    } catch (error) {
      if (!(error instanceof ScopeNotReadyError)) throw error;
      // The session has not said who is signed in, so there is no key to
      // cache under. Read the way the flag-off path does.
      console.warn("[workspace-members] members read before the session resolved; not cached", {
        slug,
      });
      return null;
    }
  }

  /** Run a cached-bank read with the loading and error state `fetchMembers` keeps. */
  private async readCachedBank(
    read: () => Promise<IWorkspaceMember[]>,
  ): Promise<IWorkspaceMember[]> {
    this.isLoading = true;
    this.loadError = null;
    try {
      const members = await read();
      runInAction(() => this.projectBank(members));
      return members;
    } catch (err) {
      runInAction(() => {
        this.loadError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  private projectBank(members: IWorkspaceMember[]): void {
    if (this.projectedBank === members) return;
    this.projectedBank = members;
    this.members = members;
  }

  /**
   * Apply a confirmed member write to every cached bank (R20) that holds it,
   * so a fresh entry can never reproject the value the write replaced. A
   * member id is a workspace binding, unique across workspaces, so the match
   * needs no slug. With no client, or no entry, there is nothing to patch.
   */
  private patchCachedBanks(update: (members: IWorkspaceMember[]) => IWorkspaceMember[]): void {
    peekQueryClient()?.setQueriesData<IWorkspaceMember[]>(
      { predicate: ({ queryKey }) => isMemberBankKey(queryKey) },
      (current) => (current === undefined ? undefined : update(current)),
    );
  }

  /** Load the first page of the management list with the given server query
   *  (search / role / sort). Replaces the current page. */
  async loadMembersPage(slug: string, query: IWorkspaceMembersListQuery = {}): Promise<void> {
    const scope = this.bankScope(slug);
    if (scope) return this.loadCachedMembersPage(slug, scope, query);
    const seq = ++this.pageLoadSeq;
    this.inFlightPageLoads += 1;
    this.isPageLoading = true;
    this.pageError = null;
    this.lastPageQuery = query;
    try {
      const page = await this.rootStore.workspaceService.listMembersPage(slug, {
        ...query,
        cursor: null,
      });
      // A newer load (or a member PATCH's echoed update) has superseded this
      // one — dropping the result keeps the fresher `pagedMembers` intact.
      if (seq !== this.pageLoadSeq) return;
      runInAction(() => {
        this.pagedMembers = page.results;
        this.pageInfo = {
          totalCount: page.total_count,
          hasNext: page.next_page_results,
          nextCursor: page.next_cursor || null,
        };
      });
    } catch (err) {
      if (seq !== this.pageLoadSeq) {
        // Superseded load failed after a newer load/PATCH took over: its error is
        // no longer actionable (a fresher state is authoritative), but don't drop
        // it silently.
        console.warn(`[workspace-members] superseded members page load for ${slug} failed`, err);
        return;
      }
      runInAction(() => {
        this.pageError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.inFlightPageLoads -= 1;
        // Clear the spinner only once every load has settled, so a stale load
        // bailing out (superseded seq) never flips it off under a newer one.
        if (this.inFlightPageLoads === 0) this.isPageLoading = false;
      });
    }
  }

  /** Append the next page (DataTable infinite scroll). Re-runs the first page
   *  when nothing is loaded yet (the error-empty "Try again" path). */
  async loadMoreMembers(slug: string): Promise<void> {
    if (this.pagedMembers.length === 0) {
      await this.loadMembersPage(slug, this.lastPageQuery);
      return;
    }
    if (!this.pageInfo.hasNext || !this.pageInfo.nextCursor || this.isLoadingMore) return;
    const scope = this.bankScope(slug);
    if (scope) return this.loadMoreCachedMembers(slug, scope);
    this.isLoadingMore = true;
    this.pageError = null;
    try {
      const page = await this.rootStore.workspaceService.listMembersPage(slug, {
        ...this.lastPageQuery,
        cursor: this.pageInfo.nextCursor,
      });
      runInAction(() => {
        const seen = new Set(this.pagedMembers.map((m) => m.id));
        this.pagedMembers = [...this.pagedMembers, ...page.results.filter((m) => !seen.has(m.id))];
        this.pageInfo = {
          totalCount: page.total_count,
          hasNext: page.next_page_results,
          nextCursor: page.next_cursor || null,
        };
      });
    } catch (err) {
      runInAction(() => {
        this.pageError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoadingMore = false;
      });
    }
  }

  /**
   * The flag-on first page: the members table's pages for `query` (R21).
   * A fresh entry answers with no request, keeping any pages already scrolled
   * in; a stale one re-reads its first page only, as the flag-off load does.
   * The same `pageLoadSeq` rule applies: a newer load, or a member write that
   * landed meanwhile, wins over this answer.
   */
  private async loadCachedMembersPage(
    slug: string,
    scope: QueryScope,
    query: IWorkspaceMembersListQuery,
  ): Promise<void> {
    const seq = ++this.pageLoadSeq;
    this.inFlightPageLoads += 1;
    this.isPageLoading = true;
    this.pageError = null;
    this.lastPageQuery = query;
    try {
      const data = await getQueryClient().fetchInfiniteQuery({
        ...membersPageQuery(this.rootStore.workspaceService, scope, query),
        pages: 1,
      });
      if (seq !== this.pageLoadSeq) return;
      runInAction(() => this.projectPages(data));
    } catch (err) {
      if (seq !== this.pageLoadSeq || isCancelledError(err)) {
        // Superseded, or cancelled by a member write (`cancelPageReads`): a
        // fresher state is authoritative, but don't drop the error silently.
        console.warn(`[workspace-members] superseded members page load for ${slug} failed`, err);
        return;
      }
      runInAction(() => {
        this.pageError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.inFlightPageLoads -= 1;
        if (this.inFlightPageLoads === 0) this.isPageLoading = false;
      });
    }
  }

  /** The flag-on next page: appended to the cached pages (R21) of the current query. */
  private async loadMoreCachedMembers(slug: string, scope: QueryScope): Promise<void> {
    const seq = this.pageLoadSeq;
    this.isLoadingMore = true;
    this.pageError = null;
    try {
      const observer = new InfiniteQueryObserver(
        getQueryClient(),
        membersPageQuery(this.rootStore.workspaceService, scope, this.lastPageQuery),
      );
      // Join a read of this entry already in flight rather than restart it.
      const result = await observer.fetchNextPage({ cancelRefetch: false, throwOnError: true });
      if (seq !== this.pageLoadSeq || !result.data) return;
      const data = result.data;
      runInAction(() => this.projectPages(data));
    } catch (err) {
      if (isCancelledError(err)) {
        console.warn(`[workspace-members] next members page for ${slug} was cancelled`, err);
        return;
      }
      runInAction(() => {
        this.pageError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoadingMore = false;
      });
    }
  }

  /** Show the cached pages: every row once, in page order, and the last page's cursor. */
  private projectPages(data: InfiniteData<IWorkspaceMembersPage>): void {
    const seen = new Set<string>();
    const rows: IWorkspaceMember[] = [];
    for (const page of data.pages) {
      for (const member of page.results) {
        if (seen.has(member.id)) continue;
        seen.add(member.id);
        rows.push(member);
      }
    }
    const last = data.pages[data.pages.length - 1];
    this.pagedMembers = rows;
    this.pageInfo = {
      totalCount: last?.total_count ?? 0,
      hasNext: last?.next_page_results ?? false,
      nextCursor: last?.next_cursor || null,
    };
  }

  /**
   * Apply a confirmed member write to every cached members page (R21), the
   * way {@link patchCachedBanks} does for the bank.
   */
  private patchCachedPages(update: (page: IWorkspaceMembersPage) => IWorkspaceMembersPage): void {
    peekQueryClient()?.setQueriesData<InfiniteData<IWorkspaceMembersPage>>(
      { predicate: ({ queryKey }) => isMembersPageKey(queryKey) },
      (current) =>
        current === undefined ? undefined : { ...current, pages: current.pages.map(update) },
    );
  }

  /**
   * Mark `slug`'s cached member bank (R20) and every members page (R21)
   * stale, without reading: for a write that changed member rows in a way
   * this store cannot patch. The next load reads them again. Nothing is
   * cached with the `query-cache` flag off, so it does nothing then.
   */
  invalidateCachedMembers(slug: string): Promise<void> {
    const scope = this.bankScope(slug);
    if (!scope) return Promise.resolve();
    return invalidateScoped(getQueryClient(), scope, "members");
  }

  /**
   * Mark the team rosters a member change reaches stale (contract §3.3): the
   * team lists that embed rosters (R14 with members), every cached roster of
   * the workspace (R17), and each named member's team list (R19). Which teams
   * a member was on is not on the member row (it carries team names only), so
   * every cached roster is included rather than a guessed subset. Nothing is
   * cached with the `query-cache` flag off, so it does nothing then.
   */
  private invalidateTeamRostersFor(slug: string, memberIds: readonly string[]): Promise<void> {
    const scope = this.bankScope(slug);
    if (!scope) return Promise.resolve();
    const client = getQueryClient();
    const teamIds = client
      .getQueryCache()
      .findAll({ queryKey: scopedKey(scope, "team") })
      .map(({ queryKey }) => teamIdOfRosterKey(queryKey))
      .filter((teamId): teamId is string => teamId !== null);
    return invalidateTeamRosters(client, scope, { teamIds, memberIds });
  }

  /**
   * Cancel the members page reads in flight, the cache's form of bumping
   * `pageLoadSeq` (#2368): a read that started before a write would land the
   * page it was sent for over the write. Query reverts each entry to what it
   * held, including any patch made since, so nothing older is cached.
   */
  private cancelPageReads(): void {
    peekQueryClient()
      ?.cancelQueries({ predicate: ({ queryKey }) => isMembersPageKey(queryKey) })
      .catch((error: unknown) => {
        console.warn("[workspace-members] cancelling members page reads failed", error);
      });
  }

  /** Export the members list as CSV, scoped to the current page query and (when
   *  `ids` is non-empty) restricted to the selected rows. */
  exportMembersCsv(slug: string, ids?: string[], columns?: string[]): Promise<Response> {
    const body: { ids?: string[]; columns?: string[] } = {};
    if (ids && ids.length > 0) body.ids = ids;
    if (columns && columns.length > 0) body.columns = columns;
    return this.rootStore.workspaceService.exportMembers(slug, body, {
      q: this.lastPageQuery.q,
      role: this.lastPageQuery.role,
      order_by: this.lastPageQuery.order_by,
    });
  }

  pollExport(slug: string, token: string): Promise<string | null> {
    return pollExportStatus(token, (tok) =>
      this.rootStore.workspaceService.getExportStatus(slug, tok),
    );
  }

  async fetchInvitations(slug: string): Promise<IWorkspaceMemberInvitation[]> {
    this.isInvitationsLoading = true;
    this.invitationsLoadError = null;
    try {
      const invitations = await this.rootStore.workspaceService.listInvitations(slug);
      runInAction(() => {
        this.invitations = invitations;
      });
      return invitations;
    } catch (error) {
      // Logged by the caller (the members loader wraps this in
      // `.catch(logLoaderFailure(...))`) — this only records the state the
      // Pending Invitations tab reads for its error view (NG-2490).
      runInAction(() => {
        this.invitationsLoadError = error instanceof Error ? error : new Error(String(error));
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isInvitationsLoading = false;
      });
    }
  }

  async inviteMembers(
    slug: string,
    data: IWorkspaceBulkInviteFormData,
    source: AnalyticsSource = "page",
  ): Promise<IWorkspaceMemberInvitation[]> {
    this.isInviting = true;
    try {
      const invitations = await this.rootStore.workspaceService.inviteMembers(slug, data);
      // Before the refetch, so a failed reload doesn't drop it (NG-2063).
      track("workspace_member_invited", { count: invitations.length, source });
      await this.fetchInvitations(slug);
      return invitations;
    } finally {
      runInAction(() => {
        this.isInviting = false;
      });
    }
  }

  /**
   * Bulk-import members from parsed CSV rows (NG-1307, epic NG-1288).
   *
   * Unlike {@link inviteMembers} this creates the accounts outright, so the
   * people it creates are already in the members list rather than in
   * `invitations`. That is why a successful import reloads the management page
   * rather than `fetchInvitations`: a `dry_run` writes nothing, so it
   * deliberately reloads nothing either.
   *
   * **`send_welcome_email` does not change that** (NG-1304). It mails everybody
   * the import brought in, but the email is a "you have been added" notice and
   * writes no invitation — deliberately, because a pending invite for somebody
   * already a member would sit in that panel for good and would make the *next*
   * genuine invitation to them a silent no-op. So `invitations` is untouched
   * whichever way the switch is set, and nothing here needs to refetch it.
   */
  async bulkImportUsers(slug: string, body: TUserImportRequest): Promise<TUserImportResult> {
    const report = await this.rootStore.workspaceService.bulkImportUsers(slug, body);
    // Updated rows count as much as created ones (NG-1297): a file that only
    // corrects people already in the list changes names and roles the table is
    // showing right now, so reloading on ``created_count`` alone left the admin
    // looking at the values they had just replaced.
    if (!report.dry_run && report.created_count + report.updated_count > 0) {
      // Best-effort, and deliberately not allowed to reject: the import has
      // already happened, so letting a failed *reload* propagate would have the
      // dialog report a successful import as a failure and invite the admin to
      // run it again. The stale list costs them a refresh; a second import would
      // not. Nothing is hidden by this: `loadMembersPage` sets `pageError` before
      // it rethrows, so the members table still shows its own error state.
      try {
        // With the query-cache flag on, the bank and the pages it would
        // reload may still be fresh in the cache, and they predate the import.
        await this.invalidateCachedMembers(slug);
        // People it placed on teams are now on those teams' rosters.
        this.invalidateTeamRostersFor(slug, []).catch((error: unknown) => {
          console.warn(
            `[workspace-members] marking team rosters stale after import failed for workspace ${slug}`,
            error,
          );
        });
        await this.loadMembersPage(slug, this.lastPageQuery);
      } catch (err) {
        console.warn(
          `[workspace-members] members list reload after import failed for workspace ${slug}; the import itself succeeded`,
          err,
        );
      }
    }
    // Not on a dry run. The preview (NG-1308) goes through this same method, so
    // an unguarded `track` counts every import twice — once when the admin is
    // still deciding — and counts an import they looked at and abandoned as one
    // that happened. The reload above is already guarded for the same reason.
    if (!report.dry_run) {
      track("workspace_members_bulk_imported", {
        created: report.created_count,
        updated: report.updated_count,
        skipped: report.skipped_count,
        errors: report.error_count,
      });
    }
    return report;
  }

  async updateMember(
    slug: string,
    memberId: string,
    data: Partial<IWorkspaceMember>,
    source: AnalyticsSource = "page",
  ): Promise<IWorkspaceMember> {
    this.activeMutationMemberId = memberId;
    // Read before the write: `fields` is what this save changed (NG-2063). A
    // role is named only as a changed field, since a custom role's slug can
    // carry its name.
    const before =
      this.pagedMembers.find((m) => m.id === memberId) ??
      this.members.find((m) => m.id === memberId);
    const fields = changedFields(data, before);
    try {
      const updated = await this.rootStore.workspaceService.updateMember(slug, memberId, data);
      runInAction(() => {
        this.members = this.members.map((member) => (member.id === memberId ? updated : member));
        this.patchCachedBanks((members) =>
          members.map((member) => (member.id === memberId ? updated : member)),
        );
        this.pagedMembers = this.pagedMembers.map((m) => (m.id === memberId ? updated : m));
        this.cancelPageReads();
        this.patchCachedPages((page) => ({
          ...page,
          results: page.results.map((m) => (m.id === memberId ? updated : m)),
        }));
        // Invalidate any first-page load already in flight so its (pre-write)
        // result can't land afterwards and revert this echoed update — the cell
        // reflects the new value without a reload (#2368).
        this.pageLoadSeq += 1;
        // A role change can shift which roles are (in)eligible — drop the cache
        // so the next picker open re-fetches.
        if (data.role !== undefined) this.forgetEligibility(slug, memberId);
      });
      track("workspace_member_updated", { member_id: memberId, fields, source });
      return updated;
    } finally {
      runInAction(() => {
        this.activeMutationMemberId = null;
      });
    }
  }

  async removeMember(
    slug: string,
    memberId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.activeMutationMemberId = memberId;
    try {
      await this.rootStore.workspaceService.deleteMember(slug, memberId);
      track("workspace_member_removed", { member_id: memberId, source });
      // Team rosters still list the removed person until they read again.
      this.invalidateTeamRostersFor(slug, [memberId]).catch((error: unknown) => {
        console.warn(
          `[workspace-members] marking team rosters stale after removing ${memberId} failed`,
          { slug },
          error,
        );
      });
      runInAction(() => {
        this.members = this.members.filter((member) => member.id !== memberId);
        this.patchCachedBanks((members) => members.filter((member) => member.id !== memberId));
        const before = this.pagedMembers.length;
        this.pagedMembers = this.pagedMembers.filter((member) => member.id !== memberId);
        if (this.pagedMembers.length < before) {
          this.pageInfo = {
            ...this.pageInfo,
            totalCount: Math.max(0, this.pageInfo.totalCount - 1),
          };
        }
        this.patchCachedPages((page) => {
          const results = page.results.filter((member) => member.id !== memberId);
          return results.length === page.results.length
            ? page
            : { ...page, results, total_count: Math.max(0, page.total_count - 1) };
        });
      });
    } finally {
      runInAction(() => {
        this.activeMutationMemberId = null;
      });
    }
  }

  async revokeInvitation(
    slug: string,
    invitationId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.activeInvitationId = invitationId;
    try {
      await this.rootStore.workspaceService.deleteInvitation(slug, invitationId);
      track("workspace_invitation_revoked", { invitation_id: invitationId, source });
      runInAction(() => {
        this.invitations = this.invitations.filter((invitation) => invitation.id !== invitationId);
      });
    } finally {
      runInAction(() => {
        this.activeInvitationId = null;
      });
    }
  }

  /** Re-send a pending invite's email and restart its expiry clock (NG-2490). */
  async resendInvitation(
    slug: string,
    invitationId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.activeInvitationId = invitationId;
    try {
      const updated = await this.rootStore.workspaceService.resendInvitation(slug, invitationId);
      track("workspace_invitation_resent", { invitation_id: invitationId, source });
      runInAction(() => {
        this.invitations = this.invitations.map((invitation) =>
          invitation.id === invitationId ? updated : invitation,
        );
      });
    } finally {
      runInAction(() => {
        this.activeInvitationId = null;
      });
    }
  }

  /** Edit a pending invite's role and/or teams (NG-2490). `team_ids` is a
   *  full-set replace, mirroring the invite dialog's own team handling. */
  async patchInvitation(
    slug: string,
    invitationId: string,
    data: { role?: string; team_ids?: string[] },
  ): Promise<void> {
    this.activeInvitationId = invitationId;
    try {
      const updated = await this.rootStore.workspaceService.updateInvitation(
        slug,
        invitationId,
        data,
      );
      runInAction(() => {
        this.invitations = this.invitations.map((invitation) =>
          invitation.id === invitationId ? updated : invitation,
        );
      });
    } finally {
      runInAction(() => {
        this.activeInvitationId = null;
      });
    }
  }
}
